import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { skipToken, useMutation, useQuery } from '@tanstack/react-query';
import { Gauge, RefreshCw } from 'lucide-react';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { Card } from '@shared/ui/containers/Card';
import { Button } from '@shared/ui/primitives/Button';
import { toast } from '@shared/ui/feedback/Toast';
import { HeroHeader } from '@shared/ui/headers/HeroHeader';
import {
  fetchLinkActions,
  fetchMyUtilityLinks,
  fetchMyUtilityTypes,
  invokeLinkAction,
  type UsageActionData,
  type UtilityAction,
  type UtilityActionResult,
} from './api';
import { UsageChart } from './UsageChart';
import { getUtilityPresentation, utilityIconBg } from './utilityPresentation';
import {
  formatTrendPct,
  formatUsageTotal,
  splitWeekTotals,
  weekOverWeekDeltaPct,
} from './utilityUsageMath';
import { useUtilityLinkUsage, utilityUsageQueryKey } from './useUtilityLinkUsage';
import { parseUsageActionMeta } from './utilityActionMeta';
import { usePropertyStore } from '@shared/stores/propertyStore';

const USAGE_ACTION_KEY = 'get_usage';

/**
 * Utility detail screen. Discovers the actions advertised by the linked
 * device's handler and renders one card per action using its `render_hint`.
 * The screen knows nothing about specific handlers — Ontec or otherwise —
 * so a new handler simply appears here the moment it's wired backend-side.
 */
export function UtilityDetail(): JSX.Element {
  const { linkId } = useParams<{ linkId: string }>();

  const linksQuery = useQuery({
    queryKey: ['my-utility-links'],
    queryFn: fetchMyUtilityLinks,
    staleTime: 60_000,
  });
  const typesQuery = useQuery({
    queryKey: ['my-utility-types'],
    queryFn: fetchMyUtilityTypes,
    staleTime: 5 * 60_000,
  });
  const link = (linksQuery.data ?? []).find((l) => l.link_id === linkId);
  const actionsQuery = useQuery({
    queryKey: ['my-utility-link-actions', linkId],
    queryFn: linkId && link?.device_id ? () => fetchLinkActions(linkId) : skipToken,
  });

  const utilityName = useMemo(() => {
    if (!link) return 'Utility';
    return (typesQuery.data ?? []).find((t) => t.utility_type_id === link.utility_type_id)?.name ?? 'Utility';
  }, [link, typesQuery.data]);
  // Friendly register labels advertised by the chart action's handler meta —
  // used to humanise raw register codes (e.g. in the header identifier).
  const registerLabels = useMemo(() => {
    const chartAction = (actionsQuery.data ?? []).find(
      (a) => a.render_hint === 'chart' && a.action_key === USAGE_ACTION_KEY,
    );
    return chartAction ? parseUsageActionMeta(chartAction)?.registerLabels : undefined;
  }, [actionsQuery.data]);
  const linkNeedsDeviceResolution = Boolean(link && !link.device_id);

  return (
    <div className="min-h-full bg-paper">
      <HeroHeader
        eyebrow="Utility"
        title={utilityName}
        subtitle={link ? identifierSummary(link.identifier_payload, registerLabels) ?? 'No identifier on file.' : undefined}
        backTo="/app/utilities"
        backBehavior="parent"
      />

      <div className="flex flex-col gap-4 px-5 pt-5 pb-safe-or-6">
        {linkNeedsDeviceResolution ? (
          <Card noGrain className="p-5 text-center">
            <Gauge className="mx-auto h-7 w-7 text-ink-muted" strokeWidth={1.5} />
            <p className="mt-2 font-display text-headline text-ink">This utility is not attached to a device yet</p>
            <p className="mt-1 text-body-sm text-ink-secondary">
              Ask the estate team to finish linking the meter on the backend, then try again.
            </p>
          </Card>
        ) : actionsQuery.isLoading ? (
          <>
            <Skeleton className="h-60 w-full" />
            <Skeleton className="h-24 w-full" />
          </>
        ) : actionsQuery.isError ? (
          <ErrorState onRetry={() => actionsQuery.refetch()} />
        ) : (actionsQuery.data ?? []).length === 0 ? (
          <Card noGrain className="p-5 text-center">
            <Gauge className="mx-auto h-7 w-7 text-ink-muted" strokeWidth={1.5} />
            <p className="mt-2 font-display text-headline text-ink">No actions available</p>
            <p className="mt-1 text-body-sm text-ink-secondary">
              The integration for this device isn't configured to expose any actions yet.
            </p>
          </Card>
        ) : (
          (actionsQuery.data ?? []).map((action) => (
            <ActionCard
              key={action.action_key}
              linkId={linkId!}
              action={action}
              utilityName={utilityName}
            />
          ))
        )}
      </div>
    </div>
  );
}

/* ── Per-action cards ──────────────────────────────────────────── */

function ActionCard({
  linkId,
  action,
  utilityName,
}: {
  linkId: string;
  action: UtilityAction;
  utilityName: string;
}): JSX.Element {
  if (action.render_hint === 'chart' && action.action_key === USAGE_ACTION_KEY) {
    return <UsageActionCard linkId={linkId} action={action} utilityName={utilityName} />;
  }
  if (action.render_hint === 'gauge') {
    return <GaugeActionCard linkId={linkId} action={action} />;
  }
  if (action.render_hint === 'button') {
    return <ButtonActionCard linkId={linkId} action={action} />;
  }
  if (action.render_hint === 'iframe') {
    return <IframeActionCard linkId={linkId} action={action} />;
  }
  // Default: list-style or unknown render_hint — generic invoke + JSON view.
  return <ListActionCard linkId={linkId} action={action} />;
}

function UsageActionCard({
  linkId,
  action,
  utilityName,
}: {
  linkId: string;
  action: UtilityAction;
  utilityName: string;
}): JSX.Element {
  const meta = useMemo(() => parseUsageActionMeta(action), [action]);
  const activePropertyId = usePropertyStore((s) => s.activePropertyId);
  const [timeframe, setTimeframe] = useState<string>(() => meta?.defaultTimeframe ?? '7d');
  const [mode, setMode] = useState<'cumulative' | 'delta'>(() => meta?.defaultViewMode ?? 'delta');
  const [register, setRegister] = useState<string | undefined>(undefined);
  // Union of every register code seen for this meter. Selecting a register
  // filters the query (so data.registers shrinks to one) — we must NOT lose the
  // other toggle options, so keep them in a stable list that only grows.
  const [knownRegisters, setKnownRegisters] = useState<string[]>([]);
  const presentation = getUtilityPresentation(utilityName, {
    utilityKind: meta?.utilityKind,
    chartKind: meta?.chartKind,
    color: meta?.presentationColor,
  });
  const HeaderIcon = presentation.icon;
  const selectedRegisters = register ? [register] : undefined;

  const usageQuery = useQuery({
    queryKey: utilityUsageQueryKey(linkId, timeframe, activePropertyId, selectedRegisters),
    queryFn: async () => {
      const params: Record<string, unknown> = { timeframe };
      if (selectedRegisters?.length) {
        params.register_codes = selectedRegisters;
      }
      const res = await invokeLinkAction<UsageActionData>(linkId, action.action_key, params);
      if (!res.success || !res.data) throw new Error(res.message ?? 'Action failed');
      return res.data;
    },
    enabled: !!meta,
    staleTime: 60_000,
  });

  // Reset the known-register list when switching meters.
  useEffect(() => {
    setKnownRegisters([]);
    setRegister(undefined);
  }, [linkId]);

  // Grow the union as registers are discovered (the unfiltered first load has them all).
  useEffect(() => {
    const regs = usageQuery.data?.registers;
    if (!regs || regs.length === 0) return;
    setKnownRegisters((prev) => {
      const union = Array.from(new Set([...prev, ...regs])).sort();
      return union.length === prev.length ? prev : union;
    });
  }, [usageQuery.data?.registers]);

  const showWeekSummary = meta?.detailSummary.showForTimeframes.includes(timeframe) ?? false;
  const comparisonTimeframe = meta?.detailSummary.comparisonTimeframe ?? '';
  const usageComparisonQuery = useUtilityLinkUsage(linkId, comparisonTimeframe, {
    enabled: showWeekSummary && !!comparisonTimeframe,
    registerCodes: selectedRegisters,
  });

  if (!meta) {
    return (
      <Card noGrain className="rounded-2xl p-4 shadow-sm">
        <p className="font-display text-headline text-ink">{action.label}</p>
        <p className="mt-2 text-body-sm text-ink-muted">Usage chart configuration is not available for this meter.</p>
      </Card>
    );
  }

  const data = usageQuery.data;
  // Toggle options come from the stable union so they don't vanish once a single
  // register is selected (which filters the response down to one).
  const registers = knownRegisters.length > 0 ? knownRegisters : data?.registers ?? [];
  const activeRegister = register ?? data?.primary_register ?? registers[0];

  const weekSummary = useMemo(() => {
    if (!showWeekSummary || !data) return null;
    const comparisonSeries = usageComparisonQuery.data?.series ?? [];
    const referenceMs = (() => {
      const iso = data.to ?? usageComparisonQuery.data?.to;
      if (!iso) return Date.now();
      const ms = new Date(iso).getTime();
      return Number.isFinite(ms) ? ms : Date.now();
    })();
    const { currentWeek, priorWeek } = splitWeekTotals(comparisonSeries, activeRegister, referenceMs);
    const total = formatUsageTotal(data.total_consumption, data.unit);
    const deltaPct = weekOverWeekDeltaPct(currentWeek, priorWeek);
    return { total, deltaPct };
  }, [showWeekSummary, data, usageComparisonQuery.data, activeRegister]);

  return (
    <Card noGrain className="rounded-2xl p-4 shadow-sm">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-pill"
            style={{ backgroundColor: utilityIconBg(presentation.color) }}
          >
            <HeaderIcon className="h-4.5 w-4.5" strokeWidth={1.75} style={{ color: presentation.color }} />
          </span>
          <div className="min-w-0">
            <p className="font-display text-headline text-ink truncate">{action.label}</p>
            {action.description ? (
              <p className="mt-0.5 text-caption text-ink-muted">{action.description}</p>
            ) : (
              <p className="mt-0.5 text-caption text-ink-muted">{utilityName}</p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => void usageQuery.refetch()}
          disabled={usageQuery.isFetching}
          aria-label="Refresh"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sunken text-ink-secondary ring-1 ring-hairline active:bg-hairline disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${usageQuery.isFetching ? 'animate-spin' : ''}`} strokeWidth={2} />
        </button>
      </div>

      {weekSummary ? (
        <div className="mb-3 flex items-baseline justify-between rounded-lg bg-sunken px-3 py-2">
          <span className="text-caption text-ink-muted">This week</span>
          <div className="text-right">
            <p className="font-bold text-ink">
              {weekSummary.total.amount}
              {weekSummary.total.unit ? (
                <span className="text-caption font-medium text-ink-muted"> {weekSummary.total.unit}</span>
              ) : null}
            </p>
            <p
              className="text-caption font-medium"
              style={{
                color:
                  weekSummary.deltaPct === null
                    ? 'var(--ink-muted)'
                    : weekSummary.deltaPct < 0
                      ? 'var(--success)'
                      : 'var(--danger)',
              }}
            >
              {formatTrendPct(weekSummary.deltaPct)}
            </p>
          </div>
        </div>
      ) : null}

      {/* Timeframe pills — single horizontally-scrollable row (driven by meta). */}
      <div className="-mx-1 mb-2 flex gap-1 overflow-x-auto px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {(meta?.timeframeOptions ?? []).map((tf) => (
          <button
            key={tf.value}
            type="button"
            onClick={() => setTimeframe(tf.value)}
            className={
              'shrink-0 whitespace-nowrap rounded-pill px-3 py-1 text-caption font-medium transition-colors ' +
              (timeframe === tf.value
                ? 'bg-primary-700 text-white'
                : 'bg-sunken text-ink-secondary')
            }
          >
            {tf.label}
          </button>
        ))}
      </div>
      {(meta?.viewModes.length ?? 0) > 1 ? (
        <div className="mb-3 flex justify-end">
          <button
            type="button"
            onClick={() => setMode((m) => (m === 'cumulative' ? 'delta' : 'cumulative'))}
            className="rounded-pill border border-hairline-strong px-3 py-1 text-caption font-medium text-ink-secondary"
          >
            {meta?.viewModes.find((v) => v.value === mode)?.label ??
              (mode === 'cumulative' ? 'Cumulative' : 'Per-period')}
          </button>
        </div>
      ) : null}

      {usageQuery.isLoading ? (
        <Skeleton className="h-44 w-full" />
      ) : usageQuery.isError ? (
        <p className="text-body-sm text-danger">
          {usageQuery.error instanceof Error ? usageQuery.error.message : 'Could not load usage'}
        </p>
      ) : data ? (
        <>
          {registers.length > 1 ? (
            <div className="mb-2 flex gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {registers.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRegister(r)}
                  className={
                    'shrink-0 whitespace-nowrap rounded-pill px-2.5 py-0.5 text-caption font-medium transition-colors ' +
                    ((activeRegister ?? '') === r
                      ? 'bg-primary-700 text-white'
                      : 'bg-sunken text-ink-secondary')
                  }
                >
                  {data?.register_labels?.[r] ?? meta?.registerLabels?.[r] ?? r}
                </button>
              ))}
            </div>
          ) : null}
          <UsageChart
            series={data.series}
            registerCode={activeRegister}
            mode={mode}
            utilityName={utilityName}
            variant={presentation.chartKind}
            color={presentation.color}
          />
          <div className="mt-3 flex items-baseline justify-between text-body-sm">
            <span className="text-ink-muted">Total this period</span>
            <span className="font-display text-headline text-ink">
              {data.total_consumption.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              {data.unit ? ` ${data.unit}` : ''}
            </span>
          </div>
        </>
      ) : (
        <p className="text-body-sm text-ink-muted">No data yet.</p>
      )}
    </Card>
  );
}

function GaugeActionCard({ linkId, action }: { linkId: string; action: UtilityAction }): JSX.Element {
  const query = useQuery({
    queryKey: ['utility-link-action', linkId, action.action_key],
    queryFn: () => invokeLinkAction(linkId, action.action_key, {}),
  });
  const value = extractScalar(query.data?.data);
  return (
    <Card noGrain className="p-4 text-center">
      <p className="font-display text-headline text-ink">{action.label}</p>
      <p className="mt-2 font-display text-display-lg text-primary-700">{value ?? '—'}</p>
      {action.description ? <p className="mt-1 text-caption text-ink-muted">{action.description}</p> : null}
    </Card>
  );
}

function ButtonActionCard({ linkId, action }: { linkId: string; action: UtilityAction }): JSX.Element {
  const mutation = useMutation({
    mutationFn: () => invokeLinkAction(linkId, action.action_key, {}),
    onSuccess: (res: UtilityActionResult) => {
      if (res.success) toast.success(res.message ?? `${action.label} succeeded`);
      else toast.danger(res.message ?? `${action.label} failed`);
    },
    onError: (e) => toast.danger(e instanceof Error ? e.message : `${action.label} failed`),
  });
  return (
    <Card noGrain className="p-4">
      <p className="font-display text-headline text-ink">{action.label}</p>
      {action.description ? <p className="mt-1 text-body-sm text-ink-secondary">{action.description}</p> : null}
      <Button
        variant="primary"
        size="md"
        width="full"
        className="mt-3"
        loading={mutation.isPending}
        onClick={() => mutation.mutate()}
      >
        Run
      </Button>
    </Card>
  );
}

function IframeActionCard({ linkId, action }: { linkId: string; action: UtilityAction }): JSX.Element {
  const query = useQuery({
    queryKey: ['utility-link-action', linkId, action.action_key],
    queryFn: () => invokeLinkAction<{ url?: string }>(linkId, action.action_key, {}),
  });
  const url = (query.data?.data as { url?: string } | undefined)?.url;
  return (
    <Card noGrain className="overflow-hidden p-0">
      <div className="p-4">
        <p className="font-display text-headline text-ink">{action.label}</p>
        {action.description ? <p className="mt-1 text-body-sm text-ink-secondary">{action.description}</p> : null}
      </div>
      {url ? (
        <iframe src={url} title={action.label} className="h-72 w-full border-0" />
      ) : query.isLoading ? (
        <Skeleton className="h-72 w-full" />
      ) : (
        <p className="px-4 pb-4 text-body-sm text-ink-muted">No content available.</p>
      )}
    </Card>
  );
}

function ListActionCard({ linkId, action }: { linkId: string; action: UtilityAction }): JSX.Element {
  const query = useQuery({
    queryKey: ['utility-link-action', linkId, action.action_key],
    queryFn: () => invokeLinkAction(linkId, action.action_key, {}),
  });
  const rows = useMemo(() => normaliseToRows(query.data?.data), [query.data]);
  return (
    <Card noGrain className="p-4">
      <p className="font-display text-headline text-ink">{action.label}</p>
      {action.description ? <p className="mt-1 text-body-sm text-ink-secondary">{action.description}</p> : null}
      {query.isLoading ? (
        <Skeleton className="mt-3 h-24 w-full" />
      ) : rows.length === 0 ? (
        <p className="mt-3 text-body-sm text-ink-muted">No data.</p>
      ) : (
        <ul className="mt-3 divide-y divide-hairline">
          {rows.map((r, i) => (
            <li key={i} className="flex items-baseline justify-between gap-3 py-1.5 text-body-sm">
              <span className="text-ink-secondary">{r.label}</span>
              <span className="font-mono text-ink">{r.value}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/* ── helpers ───────────────────────────────────────────────────── */

function identifierSummary(
  payload: Record<string, unknown> | null | undefined,
  registerLabels?: Record<string, string>,
): string | undefined {
  if (!payload || typeof payload !== 'object') return undefined;
  const parts: string[] = [];
  for (const [key, value] of Object.entries(payload)) {
    if (value === null || value === undefined || value === '') continue;
    // Map any register codes in the value to their friendly labels so the
    // header reads "Energy imported, Energy exported" rather than raw codes.
    const display = registerLabels
      ? String(value)
          .split(',')
          .map((tok) => registerLabels[tok.trim()] ?? tok.trim())
          .join(', ')
      : String(value);
    parts.push(`${humanise(key)}: ${display}`);
  }
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

function humanise(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function extractScalar(data: unknown): string | undefined {
  if (typeof data === 'number') return data.toLocaleString();
  if (typeof data === 'string') return data;
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    const candidate = obj.value ?? obj.total ?? obj.amount ?? obj.count ?? obj.reading;
    if (typeof candidate === 'number') return candidate.toLocaleString();
    if (typeof candidate === 'string') return candidate;
  }
  return undefined;
}

function normaliseToRows(data: unknown): Array<{ label: string; value: string }> {
  if (!data || typeof data !== 'object') return [];
  if (Array.isArray(data)) {
    return data.flatMap((item, i) => {
      if (item && typeof item === 'object') {
        return Object.entries(item).map(([k, v]) => ({ label: `${i + 1}. ${humanise(k)}`, value: stringify(v) }));
      }
      return [{ label: `Item ${i + 1}`, value: stringify(item) }];
    });
  }
  return Object.entries(data as Record<string, unknown>).map(([k, v]) => ({
    label: humanise(k),
    value: stringify(v),
  }));
}

function stringify(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}
